import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffortsManagersEmployeeTrend } from '../hooks/useEffortsDashboard';
import {
  FIRST_COL_SX,
  HEADER_CELL_SX,
  NUM_CELL_SX,
  TABLE_CARD_SX,
  TABLE_SX,
  TABLE_WRAP_SX,
  TableCardTitle,
  TableFooter,
} from './EffortsTableParts.jsx';
import { buildFyMonths } from './effortsTableStyles.js';

const TIERS = ['FLM', 'SLM', 'TLM'];
const ROWS_PER_PAGE = 50;
const DASH = '—';

const fmtWd = (v) => (v == null || !Number.isFinite(Number(v)) ? DASH : Number(v).toFixed(1));
const fmtCall = (v) => (v == null || !Number.isFinite(Number(v)) ? DASH : Number(v).toFixed(2));

export default function ManagersTrendTab() {
  const [tier, setTier] = useState('FLM');
  const [page, setPage] = useState(0);

  const { data, isLoading, isError } = useEffortsManagersEmployeeTrend(tier);

  const employees = useMemo(() => data?.employees ?? [], [data]);

  // Always render the full FY (Apr→Mar); no-data months show em dashes.
  const months = useMemo(
    () => buildFyMonths(data?.fiscalYear, data?.months),
    [data?.fiscalYear, data?.months]
  );

  // Reset to page 1 when the tier or the dataset changes.
  const lastKeyRef = useRef(null);
  const key = `${tier}|${employees.length}`;
  if (lastKeyRef.current !== key) {
    lastKeyRef.current = key;
    if (page !== 0) setPage(0);
  }

  const pageCount = Math.max(1, Math.ceil(employees.length / ROWS_PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const paged = employees.slice(
    safePage * ROWS_PER_PAGE,
    safePage * ROWS_PER_PAGE + ROWS_PER_PAGE
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#6B7280' }}>
          Manager
        </Typography>
        <FormControl size="small">
          <Select
            value={tier}
            onChange={(e) => {
              setTier(e.target.value);
              setPage(0);
            }}
            sx={{ fontSize: '12px', minWidth: 120 }}
          >
            {TIERS.map((t) => (
              <MenuItem key={t} value={t} sx={{ fontSize: '12px' }}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {isLoading ? <Alert severity="info">Loading managers trend…</Alert> : null}
      {isError ? <Alert severity="error">Failed to load managers trend.</Alert> : null}
      {!isLoading && !isError && employees.length === 0 ? (
        <Alert severity="info">No managers trend data available for {tier}.</Alert>
      ) : null}

      {!isLoading && !isError && employees.length > 0 ? (
        <Box sx={TABLE_CARD_SX}>
          <TableCardTitle
            title="Managers — Working Days &amp; Dr. Call Avg Trend"
            count={employees.length}
            countLabel={tier}
          />
          <Box sx={{ fontSize: '12px', color: '#6B7280', mb: 1.5, mt: -0.5 }}>
            {employees.length} {tier} · monthly Field Working Days (FWD) and Dr. Call Avg
          </Box>
          <TableContainer sx={{ ...TABLE_WRAP_SX, maxHeight: 560 }}>
            <Table size="small" stickyHeader sx={TABLE_SX}>
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2} sx={HEADER_CELL_SX}>Empl. Code</TableCell>
                  <TableCell rowSpan={2} sx={HEADER_CELL_SX}>Empl. Name</TableCell>
                  <TableCell rowSpan={2} sx={HEADER_CELL_SX}>Date of Joining</TableCell>
                  <TableCell rowSpan={2} align="right" sx={HEADER_CELL_SX}>Tenure in Years</TableCell>
                  <TableCell rowSpan={2} sx={HEADER_CELL_SX}>MIS HQ</TableCell>
                  {months.map((m) => (
                    <TableCell key={m.yearmonth} colSpan={2} align="center" sx={HEADER_CELL_SX}>
                      {m.label}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  {months.map((m) => [
                    <TableCell key={`${m.yearmonth}-fwd`} align="right" sx={HEADER_CELL_SX}>
                      FWD
                    </TableCell>,
                    <TableCell key={`${m.yearmonth}-call`} align="right" sx={HEADER_CELL_SX}>
                      Call Avg
                    </TableCell>,
                  ])}
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.map((emp) => {
                  const byMonth = Object.fromEntries(
                    (emp.points ?? []).map((p) => [String(p.yearmonth), p])
                  );
                  return (
                    <TableRow key={emp.employeeCode ?? emp.name} hover>
                      <TableCell sx={FIRST_COL_SX}>{emp.employeeCode ?? DASH}</TableCell>
                      <TableCell sx={{ ...FIRST_COL_SX, whiteSpace: 'nowrap' }}>
                        {emp.name ?? DASH}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{emp.joiningDate ?? DASH}</TableCell>
                      <TableCell align="right" sx={NUM_CELL_SX}>
                        {emp.tenureYears == null ? DASH : Number(emp.tenureYears).toFixed(1)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{emp.misHq ?? DASH}</TableCell>
                      {months.map((m) => {
                        const pt = byMonth[m.yearmonth];
                        return [
                          <TableCell key={`${m.yearmonth}-fwd`} align="right" sx={NUM_CELL_SX}>
                            {fmtWd(pt?.fwd ?? pt?.fieldWorkingDays)}
                          </TableCell>,
                          <TableCell key={`${m.yearmonth}-call`} align="right" sx={NUM_CELL_SX}>
                            {fmtCall(pt?.drCallAverage)}
                          </TableCell>,
                        ];
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TableFooter
            page={safePage}
            pageSize={ROWS_PER_PAGE}
            total={employees.length}
            onPageChange={setPage}
          />
        </Box>
      ) : null}
    </Box>
  );
}
